import { Button } from "@components/buttons/button";
import { Card } from "@components/cards/card";
import { ModalPayoutRequest } from "@components/modals/modal-payout-request";
import { Pagination } from "@components/pagination";
import { LoadingSkeleton } from "@components/skeleton-loading";
import {
  IPayoutRequestDTO,
  PayoutAccountTypeTypes,
  PayoutRequestStatusTypes,
} from "@hooks/dtos/IPayoutDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import {
  getPayoutAccountType,
  getPayoutMethod,
  getPayoutRequestStatus,
  payoutAccountTypes,
  payoutMethods,
  payoutRequestStatuses,
} from "@hooks/functions/Payout";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  FaBroom,
  FaExpand,
  FaMinus,
  FaPlus,
} from "react-icons/fa6";

const masks = new Masks();

interface IWithdrawSummaryDTO {
  total: number;
  paid: number;
  waiting: number;
  fees: number;
}

const emptySummary: IWithdrawSummaryDTO = {
  total: 0,
  paid: 0,
  waiting: 0,
  fees: 0,
};

export function Withdraw() {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [show, setShow] = useState<{ filter: boolean }>({ filter: false });
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [order, setOrder] = useState("DESC");
  const [status, setStatus] = useState<PayoutRequestStatusTypes | "">("");
  const [accountType, setAccountType] = useState<PayoutAccountTypeTypes | "">("");
  const [payoutMethod, setPayoutMethod] = useState<string>("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [withdraws, setWithdraws] = useState<IPayoutRequestDTO[]>([]);
  const [summary, setSummary] = useState<IWithdrawSummaryDTO>(emptySummary);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState({
    summary: false,
    history: false,
  });

  function getFilterParams(withPagination = false) {
    const params = new URLSearchParams();

    if (withPagination) {
      params.set("page", page.toString());
      params.set("take", take.toString());
      params.set("order", order);
    }

    if (status) params.set("status", status);
    if (accountType) params.set("accountType", accountType);
    if (payoutMethod) params.set("payoutMethod", payoutMethod);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);

    return params.toString();
  }

  async function getWithdrawSummary(params = getFilterParams()) {
    setIsLoading((prev) => ({ ...prev, summary: true }));

    try {
      const result = await api.get(
        `/customer/financial/history/withdrawSummary${params ? `?${params}` : ""}`,
      );

      setSummary(result.data);
    } catch (err: any) {
      setSummary(emptySummary);
      pushToastify({
        message: err?.response?.data?.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, summary: false }));
    }
  }

  async function getWithdrawList(params = getFilterParams(true)) {
    setIsLoading((prev) => ({ ...prev, history: true }));

    try {
      const result = await api.get(
        `/customer/financial/history/withdrawList?${params}`,
      );

      setWithdraws(result.data.list || []);
      setTotalPages(result.data.totalPages || 1);
    } catch (err: any) {
      setWithdraws([]);
      setTotalPages(1);
      pushToastify({
        message: err?.response?.data?.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, history: false }));
    }
  }

  function handleFilter() {
    getWithdrawSummary();

    if (page === 1) {
      getWithdrawList();
      return;
    }

    setPage(1);
  }

  function clearFilters() {
    const params = new URLSearchParams({
      page: "1",
      take: take.toString(),
      order: "DESC",
    }).toString();

    setStatus("");
    setAccountType("");
    setPayoutMethod("");
    setStartDate("");
    setEndDate("");
    setOrder("DESC");
    setPage(1);
    getWithdrawSummary("");
    getWithdrawList(params);
  }

  function handleRequestWithdraw() {
    setIsWithdrawModalOpen(true);
  }

  useEffect(() => {
    getWithdrawList();
  }, [page, take, order]);

  useEffect(() => {
    getWithdrawSummary();
  }, []);

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div
              className="card-header"
              style={{ cursor: "pointer" }}
              onClick={() => setShow((prev) => ({ ...prev, filter: !prev.filter }))}
            >
              <div className="d-flex justify-content-between">
                <div>
                  <div className="card-title">Filtrar Saques</div>
                  <div className="card-category">
                    Consulte solicitações por status, método, tipo e período.
                  </div>
                </div>
                <div>
                  {show.filter ? (
                    <FaMinus className="fs-5" title="Clique para ocultar." />
                  ) : (
                    <FaExpand className="fs-5" title="Clique para visualizar." />
                  )}
                </div>
              </div>
            </div>
            <div className={`card-body ${show.filter ? "" : "d-none"}`}>
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label small fw-semibold mb-1">
                    Status
                  </label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) =>
                      setStatus(e.target.value as PayoutRequestStatusTypes | "")
                    }
                  >
                    <option value="">Todos</option>
                    {payoutRequestStatuses.map((item) => (
                      <option key={item.type} value={item.type}>
                        {item.text}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-semibold mb-1">
                    Tipo de Saque
                  </label>
                  <select
                    className="form-select"
                    value={accountType}
                    onChange={(e) =>
                      setAccountType(e.target.value as PayoutAccountTypeTypes | "")
                    }
                  >
                    <option value="">Todos</option>
                    {payoutAccountTypes.map((item) => (
                      <option key={item.type} value={item.type}>
                        {item.text}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small fw-semibold mb-1">
                    Método
                  </label>
                  <select
                    className="form-select"
                    value={payoutMethod}
                    onChange={(e) => setPayoutMethod(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {payoutMethods.map((item) => (
                      <option key={item.type} value={item.type}>
                        {item.text}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small fw-semibold mb-1">
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small fw-semibold mb-1">
                    Data Final
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2 border-top mt-3 pt-3">
                <button className="btn btn-success px-5" onClick={handleFilter}>
                  Filtrar
                </button>
                <button className="btn btn-warning" onClick={clearFilters}>
                  <FaBroom />
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="card-title">Resumo dos Saques</div>
                  <div className="card-category">
                    Valores solicitados, pagos, pendentes e taxas.
                  </div>
                </div>
                <Button className="btn btn-success" onClick={handleRequestWithdraw}>
                  <FaPlus className="me-2" />
                  Solicitar Saque
                </Button>
              </div>
            </div>
            <div className="card-body">
              {isLoading.summary ? (
                <LoadingSkeleton count={2} />
              ) : (
                <div className="row g-3 text-center">
                  <div className="col-md-3">
                    <div className="fs-4 fw-bold">{masks.brlCurrency(summary.total)}</div>
                    <small className="text-muted">Total Solicitado</small>
                  </div>
                  <div className="col-md-3">
                    <div className="fs-4 fw-bold text-success">
                      {masks.brlCurrency(summary.paid)}
                    </div>
                    <small className="text-muted">Total Pago</small>
                  </div>
                  <div className="col-md-3">
                    <div className="fs-4 fw-bold text-warning">
                      {masks.brlCurrency(summary.waiting)}
                    </div>
                    <small className="text-muted">Aguardando</small>
                  </div>
                  <div className="col-md-3">
                    <div className="fs-4 fw-bold text-danger">
                      {masks.brlCurrency(summary.fees)}
                    </div>
                    <small className="text-muted">Taxas</small>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="row align-items-center g-3">
                <div className="col-md-8">
                  <h4 className="card-title">Histórico de Saques</h4>
                  <p className="card-category">Lista de solicitações de saque</p>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={take}
                    onChange={(e) => {
                      setTake(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={order}
                    onChange={(e) => setOrder(e.target.value)}
                  >
                    <option value="DESC">Decrescente</option>
                    <option value="ASC">Crescente</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Método</th>
                      <th>Tipo</th>
                      <th>Conta</th>
                      <th>Valor</th>
                      <th>Taxas</th>
                      <th>Status</th>
                      <th>Solicitado</th>
                      <th>Fechado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading.history ? (
                      <tr>
                        <td colSpan={9}>
                          <LoadingSkeleton count={4} />
                        </td>
                      </tr>
                    ) : withdraws.length ? (
                      withdraws.map((withdraw) => {
                        const status = getPayoutRequestStatus(withdraw.status);
                        const type = getPayoutAccountType(withdraw.account.type);
                        const method = getPayoutMethod(withdraw.payoutMethod);

                        return (
                          <tr key={withdraw.id}>
                            <td>{withdraw.id}</td>
                            <td>
                              <span
                                className={`badge badge-${method.color}`}
                                title={method.title}
                              >
                                {method.text}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge badge-${type.color}`}
                                title={type.title}
                              >
                                {type.text}
                              </span>
                            </td>
                            <td>
                              <div className="fw-semibold">
                                {withdraw.account.label || "Conta de saque"}
                              </div>
                              <small className="text-muted">
                                {withdraw.account.accountKey}
                              </small>
                            </td>
                            <td>{masks.brlCurrency(withdraw.value)}</td>
                            <td
                              title={`Taxa interna: ${masks.brlCurrency(
                                withdraw.internalFee,
                              )} | Taxa externa: ${masks.brlCurrency(
                                withdraw.externalFee,
                              )}`}
                            >
                              {masks.brlCurrency(
                                withdraw.internalFee + withdraw.externalFee,
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge badge-${status.color}`}
                                title={status.title}
                              >
                                {status.text}
                              </span>
                            </td>
                            <td>
                              {dayjs(withdraw.dateRequest).format(
                                "DD/MM/YYYY [às] HH:mm:ss",
                              )}
                            </td>
                            <td>
                              {withdraw.dateClose
                                ? dayjs(withdraw.dateClose).format(
                                    "DD/MM/YYYY [às] HH:mm:ss",
                                  )
                                : "-"}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center text-muted">
                          Nenhum saque encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                loading={isLoading.history}
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                maxPageShow={3}
              />
            </div>
          </Card>
        </div>
      </div>

      {isWithdrawModalOpen && (
        <ModalPayoutRequest
          isOpen={isWithdrawModalOpen}
          onClose={() => setIsWithdrawModalOpen(false)}
          onCreated={() => {
            getWithdrawSummary();
            getWithdrawList();
          }}
        />
      )}
    </>
  );
}

